"use client";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import maplibregl, { Map as MlMap } from "maplibre-gl";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, TopBar } from "@/components/ui";
import { branchesApi } from "@/api/services";
import { useBranch } from "@/store/branch";
import { persistSelectedBranchId } from "@/store/branch-storage";
import { useFulfillment } from "@/store/fulfillment";
import { useAddresses } from "@/hooks/useAddresses";
import { useAuth } from "@/store/auth";
import type { AppSchemasCustomerAddress } from "@/api/online-ordering-service/model";
import { scopedStorageKey } from "@/lib/storageScope";
import {
  addressDisplayLine,
  branchDisplayName as getBranchDisplayName,
} from "@/lib/fulfillment";
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_STYLE,
  DEFAULT_ZOOM,
  autocompleteAddress,
  normalizeCountryCode,
  resolvePlaceId,
  reverseGeocode,
  type ResolvedAddress,
  type PlaceSuggestion,
} from "@/lib/location";
import type { Branch } from "@/api/online-ordering-service/model";

const TEMP_ADDRESS_KEY = "v2_temp_selected_address";

type ReturnTo = "home" | "item-detail" | "new-address-form" | "checkout" | "addresses";
type DeliverabilityState = "idle" | "checking" | "deliverable" | "not_deliverable" | "unknown";

function localizedText(
  value: Record<string, unknown> | string | null | undefined,
  lang: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") return value;
  const direct = value[lang];
  if (typeof direct === "string" && direct) return direct;
  if (typeof value.en === "string" && value.en) return value.en;
  if (typeof value.ar === "string" && value.ar) return value.ar;
  for (const candidate of Object.values(value)) {
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return null;
}

function destinationFor(returnTo: ReturnTo, addressId: string | null): string {
  switch (returnTo) {
    case "home":
      return "/";
    case "item-detail":
      return "/";
    case "new-address-form":
      return addressId ? `/addresses/new?id=${encodeURIComponent(addressId)}` : "/addresses/new";
    case "checkout":
      return "/checkout";
    case "addresses":
      return "/addresses";
    default:
      return "/addresses/new";
  }
}

export function MapPickerScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { branchConfig, branchSlug } = useBranch();
  const { setMode, setManualAddress, setSelectedAddressId } = useFulfillment();
  const { isLoggedIn } = useAuth();
  const { addresses } = useAddresses();
  const tempAddressKey = useMemo(
    () => scopedStorageKey(TEMP_ADDRESS_KEY, branchSlug),
    [branchSlug],
  );
  const returnTo = (searchParams.get("returnTo") as ReturnTo | null) ?? "new-address-form";
  const addressId = searchParams.get("id") ?? searchParams.get("addressId");
  const next = searchParams.get("next");

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const ignoreNextMove = useRef(false);
  const lastGeocodeCenter = useRef<{ lat: number; lng: number } | null>(null);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geocodeSeq = useRef(0);

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [picked, setPicked] = useState<ResolvedAddress | null>(null);
  const [busy, setBusy] = useState(false);
  const [canDetectLocation, setCanDetectLocation] = useState(false);
  const [deliverability, setDeliverability] = useState<DeliverabilityState>("idle");
  const [nearestBranch, setNearestBranch] = useState<Branch | null>(null);
  const [isAddressesExpanded, setIsAddressesExpanded] = useState(false);
  // Country to bias Google Places search to (e.g. so an Egyptian user
  // searching "miami" gets Alexandria's Miami district, not Miami FL).
  // Sourced from the active branch's address; falls back to a reverse-geocode
  // of the map center (via TomTom) if the branch doesn't have one yet, and is
  // simply omitted (bias-only, no restriction) if neither is available.
  const [searchCountry, setSearchCountry] = useState<string | undefined>(undefined);

  // Best-effort country code from the branch config's address payload.
  // `branchConfig.branches[i].address` is loosely typed (raw JSON from the
  // config API) so this reads defensively rather than assuming a shape.
  const branchCountry = useMemo(() => {
    const branches = branchConfig?.branches;
    if (!Array.isArray(branches)) return undefined;
    for (const b of branches) {
      const raw = (b as { address?: { country?: unknown } } | null)?.address?.country;
      if (typeof raw === "string" && raw.trim()) {
        const normalized = normalizeCountryCode(raw);
        if (normalized) return normalized;
      }
    }
    return undefined;
  }, [branchConfig]);

  useEffect(() => {
    if (branchCountry) {
      setSearchCountry(branchCountry);
    }
  }, [branchCountry]);

  // Reverse-geocode the current map center. Throttled by the moveend handler
  // and guarded by a minimum-distance check so we don't bill Google for
  // sub-pixel drifts or rapid drags that land within ~50 m of the last fix.
  const resolveCenter = async () => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();

    const last = lastGeocodeCenter.current;
    if (last) {
      const dLat = Math.abs(c.lat - last.lat);
      const dLng = Math.abs(c.lng - last.lng);
      // ~50 m in degrees at the equator; tight enough to catch real moves,
      // loose enough to ignore jitter.
      if (dLat < 0.0005 && dLng < 0.0005) return;
    }

    const seq = ++geocodeSeq.current;
    setBusy(true);
    try {
      const result = await reverseGeocode(c.lat, c.lng);
      // Drop stale responses from superseded moves.
      if (seq !== geocodeSeq.current) return;
      setPicked(
        result ?? {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
          label: `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`,
          lat: c.lat,
          lng: c.lng,
        },
      );
      if (!branchCountry && result?.country) {
        setSearchCountry(normalizeCountryCode(result.country) || undefined);
      }
      lastGeocodeCenter.current = { lat: c.lat, lng: c.lng };
    } finally {
      if (seq === geocodeSeq.current) {
        setBusy(false);
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DEFAULT_MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      // Try to centre on user immediately (v1 behaviour: centerMap()).
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            map.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 15 });
          },
          () => {
            void resolveCenter();
          },
        );
      } else {
        void resolveCenter();
      }
    });

    map.on("moveend", () => {
      setCanDetectLocation(true);
      if (ignoreNextMove.current) {
        ignoreNextMove.current = false;
        return;
      }
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
      geocodeTimer.current = setTimeout(() => {
        geocodeTimer.current = null;
        void resolveCenter();
      }, 500);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced forward search (v1 only triggers at length >= 3).
  useEffect(() => {
    if (!search || search.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      const center = mapRef.current?.getCenter();
      const bias: [number, number] | undefined = center ? [center.lng, center.lat] : undefined;
      const results = await autocompleteAddress(search, {
        biasPosition: bias,
        maxResults: 6,
        country: searchCountry,
      });
      if (!cancelled) setSuggestions(results);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, searchCountry]);

  useEffect(() => {
    if (!picked || !branchConfig?.urlPath) {
      setDeliverability(picked ? "unknown" : "idle");
      setNearestBranch(null);
      return;
    }
    let cancelled = false;
    setDeliverability("checking");
    setNearestBranch(null);

    branchesApi
      .findNearest(picked.lat, picked.lng, branchConfig.urlPath, {
        limit: 1,
        serviceType: "delivery",
      })
      .then((res) => {
        if (cancelled) return;
        const branch = res.branches?.[0] ?? null;
        if (branch) {
          setNearestBranch(branch);
          setDeliverability("deliverable");
        } else {
          setNearestBranch(null);
          setDeliverability("not_deliverable");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[MapPicker] nearest branch lookup failed", err);
        setNearestBranch(null);
        // Do not block address creation on an API failure; backend order
        // placement remains the source of truth.
        setDeliverability("unknown");
      });

    return () => {
      cancelled = true;
    };
  }, [branchConfig?.urlPath, picked]);

  const detectCurrentLocation = () => {
    if (!navigator.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 15,
        });
      },
      (err) => console.warn("[MapPicker] geolocation failed", err),
    );
  };

  const pickSuggestion = async (s: PlaceSuggestion) => {
    setSearch("");
    setSuggestions([]);
    setBusy(true);
    const resolved = await resolvePlaceId(s.placeId);
    setBusy(false);
    if (resolved && mapRef.current) {
      setPicked(resolved);
      ignoreNextMove.current = true;
      mapRef.current.flyTo({ center: [resolved.lng, resolved.lat], zoom: 16 });
    }
  };

  const handleConfirm = () => {
    if (!picked) return;
    if (deliverability === "not_deliverable") return;
    try {
      sessionStorage.setItem(tempAddressKey, JSON.stringify(picked));
    } catch {
      // ignore
    }
    if (nearestBranch?.id) {
      persistSelectedBranchId(nearestBranch.id, nearestBranch);
    }
    setMode("delivery");
    if (isLoggedIn) {
      const match = addresses.find((a) => {
        if (!a.coordinates) return false;
        const latDiff = Math.abs(a.coordinates.latitude - picked.lat);
        const lngDiff = Math.abs(a.coordinates.longitude - picked.lng);
        return latDiff < 0.0002 && lngDiff < 0.0002;
      });
      if (match) {
        setSelectedAddressId(match.id);
        navigate(destinationFor(returnTo, match.id));
        return;
      }
    }

    const manual: AppSchemasCustomerAddress = {
      id: "manual",
      name: picked.label || picked.street,
      street: picked.street,
      city: picked.city,
      state: picked.state,
      postalCode: picked.postalCode,
      country: picked.country,
      coordinates: { latitude: picked.lat, longitude: picked.lng },
      isPrimary: false,
    };
    setManualAddress(manual);

    let dest = destinationFor(returnTo, addressId);
    if (returnTo === "new-address-form" && next) {
      const [path, query = ""] = dest.split("?");
      const params = new URLSearchParams(query);
      params.set("returnTo", next);
      dest = `${path}?${params.toString()}`;
    }
    if (returnTo === "new-address-form" || returnTo === "checkout") {
      navigate(dest, { state: { picked } });
    } else {
      navigate(dest);
    }
  };

  const onSelectSavedAddress = async (address: AppSchemasCustomerAddress) => {
    setMode("delivery");
    setSelectedAddressId(address.id);
    const coords = address.coordinates;
    if (coords && branchConfig?.urlPath) {
      try {
        const res = await branchesApi.findNearest(
          coords.latitude,
          coords.longitude,
          branchConfig.urlPath,
          { limit: 1, serviceType: "delivery" },
        );
        const branch = res.branches?.[0] ?? null;
        if (branch?.id) {
          persistSelectedBranchId(branch.id, branch);
        }
      } catch (err) {
        console.warn("[MapPicker] saved address lookup failed", err);
      }
    }
    navigate(destinationFor(returnTo, address.id));
  };

  const labelLine =
    picked?.street ||
    (picked?.label ? picked.label.split(",")[0]?.trim() : "") ||
    t("v2.map_picker.drop_pin");

  const subLineParts = picked
    ? [picked.neighborhood, picked.city, picked.state]
        .filter(Boolean)
        .filter((part, idx, arr) => arr.indexOf(part) === idx)
    : [];

  const subLine =
    subLineParts.length > 0
      ? subLineParts.join(", ")
      : picked
        ? [picked.city, picked.state, picked.postalCode].filter(Boolean).join(", ")
        : t("v2.map_picker.drop_pin_desc");
  const branchDisplayName =
    localizedText(nearestBranch?.name, i18n.language || "en") ?? nearestBranch?.areaName ?? null;
  const deliverabilityLabel =
    deliverability === "checking"
      ? t("v2.map_picker.checking_area")
      : deliverability === "deliverable"
        ? branchDisplayName
          ? t("v2.map_picker.deliverable_from", { branch: branchDisplayName })
          : t("v2.map_picker.deliverable")
        : deliverability === "not_deliverable"
          ? t("v2.map_picker.not_deliverable")
          : t("v2.map_picker.deliverability_unknown");
  const deliverabilityTone =
    deliverability === "deliverable"
      ? "success"
      : deliverability === "not_deliverable"
        ? "danger"
        : deliverability === "checking"
          ? "warn"
          : "neutral";

  return (
    <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column" }}>
      <TopBar title={t("v2.map_picker.title")} onBack={() => navigate(-1)} />

      {/* Full-bleed map underneath. The bottom drawer sits over it. */}
      <div
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          background: "var(--surface-2)",
        }}
      >
        <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
        {/* Fixed-centre pin: map moves underneath, pin stays put. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 5,
            color: "var(--primary-600)",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
          }}
        >
          <Icons.pin size={36} />
        </div>
      </div>

      {/* Bottom drawer — mirrors v1's specify-location card layout: a single
          rounded sheet anchored at the bottom that holds the resolved address,
          a search input, the "detect location" link, and the confirm button. */}
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          zIndex: 20,
          borderRadius: 18,
          background: "var(--surface-1)",
          boxShadow: "0 -10px 28px -12px rgba(0,0,0,0.2)",
          padding: 14,
          maxHeight: "70%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoggedIn && addresses.length > 0 && (
          <div
            style={{
              marginBottom: 12,
              paddingBottom: 12,
              borderBottom: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: isAddressesExpanded ? 8 : 0,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--ink-3)",
                  textTransform: "uppercase",
                }}
              >
                {t("saved_addresses")}
              </div>
              <button
                onClick={() => setIsAddressesExpanded(!isAddressesExpanded)}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--primary-600)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 6,
                  fontFamily: "inherit",
                }}
              >
                {isAddressesExpanded ? t("v2.common.hide", "Hide") : t("v2.common.show", "Show")}
              </button>
            </div>
            {isAddressesExpanded && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: 160,
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                {addresses.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => onSelectSavedAddress(a)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "var(--surface-2)",
                      border: "1px solid var(--border)",
                      cursor: "pointer",
                      textAlign: "start",
                      fontFamily: "inherit",
                    }}
                  >
                    <Icons.pin size={14} style={{ color: "var(--ink-3)" }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-1)" }}>
                        {a.name || t("v2.checkout.address_label_default")}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--ink-2)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {addressDisplayLine(a)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: "visible" }}>
          {/* Resolved location header */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "var(--primary-50)",
                display: "grid",
                placeItems: "center",
                color: "var(--primary-700)",
                flexShrink: 0,
              }}
            >
              <Icons.pin size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {busy ? t("v2.map_picker.resolving") : t("v2.map_picker.pinned_location")}
              </div>
              <div className="display"
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {labelLine}
              </div>
              {subLine && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ink-2)",
                    marginTop: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {subLine}
                </div>
              )}
              {picked && (
                <div style={{ marginTop: 6 }}>
                  <Badge tone={deliverabilityTone}>{deliverabilityLabel}</Badge>
                </div>
              )}
            </div>
          </div>

          {/* Search box (bottom UX, mirrors v1) */}
          <div style={{ position: "relative" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                height: 42,
                padding: "0 12px",
                background: "var(--surface-2)",
                borderRadius: "var(--radius-card)",
                border: "1px solid var(--border)",
              }}
            >
              <Icons.search size={18} style={{ color: "var(--ink-3)" }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("v2.map_picker.search_placeholder")}
                style={{
                  flex: 1,
                  border: 0,
                  background: "transparent",
                  outline: "none",
                  fontSize: 14,
                  fontFamily: "inherit",
                  color: "var(--ink-1)",
                }}
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setSuggestions([]);
                  }}
                  aria-label={t("v2.common.close")}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--ink-3)",
                    cursor: "pointer",
                    padding: 4,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <Icons.close size={16} />
                </button>
              )}
            </div>
            {suggestions.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "calc(100% + 8px)",
                  zIndex: 10,
                  background: "var(--surface-1)",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                  boxShadow: "0 12px 24px -10px rgba(0,0,0,0.22)",
                  overflow: "hidden",
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {suggestions.map((s, i) => (
                  <button
                    key={s.placeId}
                    onClick={() => pickSuggestion(s)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "10px 14px",
                      background: "transparent",
                      border: 0,
                      borderTop: i === 0 ? 0 : "1px solid var(--border)",
                      textAlign: "start",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <Icons.pin size={14} style={{ color: "var(--ink-2)" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--ink-1)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.label}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                        {s.secondaryText}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={detectCurrentLocation}
            disabled={!canDetectLocation}
            style={{
              marginTop: 10,
              background: "transparent",
              border: 0,
              color: canDetectLocation ? "var(--primary-600)" : "var(--ink-3)",
              fontSize: 13,
              fontWeight: 600,
              cursor: canDetectLocation ? "pointer" : "default",
              opacity: canDetectLocation ? 1 : 0.5,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            <Icons.spark size={14} /> {t("v2.map_picker.detect_location")}
          </button>

          <div style={{ marginTop: 12 }}>
            <Button
              full
              size="lg"
              onClick={handleConfirm}
              disabled={
                !picked ||
                busy ||
                deliverability === "checking" ||
                deliverability === "not_deliverable"
              }
            >
              {busy ? t("v2.map_picker.wait") : t("v2.map_picker.confirm_location")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
